import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, BookOpen } from "lucide-react";
import { RevealLine, WHATSAPP_DEFAULT, FloralCorner, EASE } from "./shared";

const HeroSection = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yBg = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const yImg = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section id="inicio" ref={ref} className="relative flex min-h-screen items-center overflow-hidden pt-24 pb-16" data-testid="hero-section">
            <motion.div style={{ y: yBg }} className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                <img src="/assets/texture.png" alt="" className="h-full w-full object-cover opacity-70" />
                <div className="drift absolute -left-32 top-10 h-[34rem] w-[34rem] rounded-full bg-lav/70 blur-3xl" />
                <div className="drift-slow absolute -right-40 bottom-0 h-[38rem] w-[38rem] rounded-full bg-serenitylight/50 blur-3xl" />
            </motion.div>

            <FloralCorner className="pointer-events-none absolute -right-4 top-20 h-44 w-44 rotate-90 text-lilac/60 sm:h-64 sm:w-64" />
            <FloralCorner className="pointer-events-none absolute -left-6 bottom-6 h-40 w-40 -scale-x-100 text-serenity/50 sm:h-56 sm:w-56" />

            <div className="mx-auto grid w-full max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-8">
                <div>
                    <h1 className="mt-2 font-serif font-bold leading-[1.02] tracking-tight text-plum">
                        <RevealLine delay={0.3} className="text-5xl sm:text-6xl lg:text-7xl">
                            Maricleia
                        </RevealLine>
                        <RevealLine delay={0.42} className="text-5xl italic sm:text-6xl lg:text-7xl">
                            <span className="bg-gradient-to-r from-lilac via-serenity to-serenitylight bg-clip-text text-transparent">Araújo</span>
                        </RevealLine>
                    </h1>

                    <RevealLine delay={0.56}>
                        <p className="mt-6 max-w-xl text-base leading-relaxed text-ink sm:text-lg">
                            Gerontóloga <span className="text-lilac">|</span> Especialista em Saúde Mental <span className="text-lilac">|</span> Assistência ao Idoso <span className="text-lilac">|</span> Terapia Ocupacional
                        </p>
                    </RevealLine>

                    <RevealLine delay={0.68}>
                        <p className="mt-4 max-w-xl font-serif text-xl italic text-plum/90 sm:text-2xl">
                            “Cuidado dedicado, Respeito, Acolhimento e Bem-estar.”
                        </p>
                    </RevealLine>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.85, ease: EASE }}
                        className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
                    >
                        <a
                            href={WHATSAPP_DEFAULT}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-button"
                            className="inline-flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-lilac via-serenity to-serenitylight px-8 py-4 text-base font-semibold text-white shadow-xl shadow-serenity/35 transition-transform duration-300 hover:scale-[1.03]"
                        >
                            <MessageCircle size={19} />
                            Agende pelo WhatsApp
                        </a>
                        <a
                            href="#livro"
                            data-testid="hero-book-button"
                            className="inline-flex items-center justify-center gap-2.5 rounded-full border border-lilac/60 bg-white/70 px-8 py-4 text-base font-semibold text-plum backdrop-blur-sm transition-all duration-300 hover:scale-[1.03] hover:border-plum/40"
                        >
                            <BookOpen size={19} />
                            Conheça o livro
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    style={{ y: yImg }}
                    initial={{ opacity: 0, scale: 0.94, y: 40 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
                    className="relative mx-auto w-full max-w-md lg:max-w-none"
                >
                    <div className="relative overflow-hidden rounded-t-[12rem] rounded-b-[2rem] border-[6px] border-white shadow-2xl shadow-plum/20">
                        <img
                            src="/assets/portrait.png"
                            alt="Maricleia Araújo, gerontóloga, em retrato profissional sobre fundo aquarela lilás"
                            className="aspect-[4/5] w-full object-cover"
                            data-testid="hero-portrait"
                        />
                        <div className="absolute inset-0 bg-lav/20 mix-blend-soft-light" />
                        <div className="absolute inset-0 bg-gradient-to-t from-plum/25 via-transparent to-lav/25" />
                    </div>

                    <motion.div
                        initial={{ opacity: 0, rotate: -12, scale: 0.7 }}
                        animate={{ opacity: 1, rotate: 8, scale: 1 }}
                        transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
                        className="absolute -right-3 -top-6 sm:-right-8"
                        data-testid="hero-author-seal"
                    >
                        <div className="seal-shine flex h-28 w-28 flex-col items-center justify-center rounded-full border border-gold/70 bg-gradient-to-br from-[#F3E5AB] via-[#D4AF37] to-[#C59B27] text-center shadow-xl shadow-gold/30 sm:h-32 sm:w-32">
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white drop-shadow-sm sm:text-[11px]">Autora</span>
                            <span className="mt-1 px-2 font-serif text-[10px] italic leading-tight text-white drop-shadow-sm sm:text-[11px]">
                                Livro Cuidados que Transformam
                            </span>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default HeroSection;
